// utils/axios.js
import axios from "axios";
import { authService } from "services/authService";
import { getStore } from "store/reducers";
import { SET_ERROR } from "store/reducers/actions";

const debug = false;

class AxiosService {
  instance;
  store;
  refreshPromise = null;
  publicRoutes = [
    "/api/auth/logout",
    "/api/auth/login",
    "/api/auth/register",
    "/api/auth/verify-otp",
    "/api/auth/forgot-password",
    "/api/auth/refresh",
    // AllowAny on the backend — a stale/invalid token here shouldn't be able
    // to 401 this request and hide the banner (see banner-not-showing
    // investigation: a bad token turned this into a silent failure).
    "/api/advertising/banners/active_banners",
  ];
  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_APP_API_URL,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    this.store = getStore();
    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor
    // SEC-019: the access token is an httpOnly cookie now (set by
    // login/verify-otp/refresh/OAuth), not something this code ever reads
    // or attaches itself — withCredentials: true (above) is what makes the
    // browser send it automatically, same as the refresh-token cookie
    // already works. Nothing left for this interceptor to do per-request.
    this.instance.interceptors.request.use(
      (config) => config,
      (error) => {
        this.store.dispatch({
          type: SET_ERROR,
          payload: "Request configuration error",
        });
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't retry refresh token requests
        // if (originalRequest.url?.includes('/api/auth/refresh/')) {
        //   console.error('🔴 Refresh token request failed:', error.response?.data);
        //   // this.store.dispatch({ type: LOGOUT });
        //   return Promise.reject(error);
        // }

        const isPublicRoute = this.publicRoutes.some((route) =>
          originalRequest.url.includes(route),
        );

        // Don't retry public routes or already retried requests
        if (isPublicRoute || originalRequest._retry) {
          return Promise.reject(error);
        }

        // Handle token expiration
        if (this.isTokenExpiredError(error) && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Multiple requests can 401 at once (e.g. a page firing many
            // parallel calls right as the access token expires). Without
            // sharing one in-flight refresh, each would independently POST
            // /api/auth/refresh/ with the same single-use refresh cookie —
            // only the first succeeds and the rest fail, silently dropping
            // whichever requests lost the race (they resolve to empty/zero
            // instead of retrying). Route every concurrent 401 through the
            // same refresh promise so there is only ever one refresh call.
            await this.getOrCreateRefreshPromise();

            // Retry — the browser already has the new access_token cookie
            // from the refresh response, no header to set here anymore.
            return this.instance(originalRequest);
          } catch (refreshError) {
            debug &&
              console.error("❌ Refresh failed:", refreshError.response?.data);
            // this.store.dispatch({ type: LOGOUT });
            if (!originalRequest.url.includes("/api/auth/logout")) {
              await authService.logout();
            }
            return Promise.reject(refreshError);
          }
        }

        // Handle other errors
        return Promise.reject(error);
      },
    );
  }

  isTokenExpiredError(error) {
    // Only 401 means expired/invalid credentials. Treating 403 as expiry
    // retried multipart uploads (FormData) and turned file fields into strings.
    return error.response?.status === 401;
  }

  async refreshToken() {
    return this.instance.post("/api/auth/refresh/");
  }

  // Ensures concurrent 401s share a single in-flight refresh call instead
  // of each firing their own (see comment at the call site). The response
  // sets a fresh access_token cookie itself (SEC-019) — nothing here needs
  // to read or store the token value, just confirm the refresh succeeded
  // before letting queued retries proceed.
  getOrCreateRefreshPromise() {
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshToken()
        .then(({ data }) => {
          if (!data.success) {
            throw new Error("Refresh failed");
          }
          return true;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  // handleRefreshFailure(error) {
  //   console.error('Token refresh failed:', error.response?.data || error.message);
  //   this.store.dispatch({ type: LOGOUT });
  //   this.store.dispatch({
  //     type: SET_ERROR,
  //     payload: 'Session expired. Please login again.'
  //   });
  // }

  // handleResponseError(error) {
  //   const errorMessage = error.response?.data?.message || error.message;
  //   this.store.dispatch({
  //     type: SET_ERROR,
  //     payload: errorMessage
  //   });
  // }

  // HTTP method wrappers
  async get(url, config = {}) {
    return this.instance.get(url, config);
  }

  async post(url, data = null, config = {}) {
    return this.instance.post(url, data, config);
  }

  async put(url, data = null, config = {}) {
    return this.instance.put(url, data, config);
  }

  async delete(url, config = {}) {
    return this.instance.delete(url, config);
  }

  async patch(url, data = null, config = {}) {
    return this.instance.patch(url, data, config);
  }
}

const axiosService = new AxiosService();
export default axiosService.instance;

// Helper functions for data fetching
export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  try {
    const response = await axiosService.get(url, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetcherPost = async (args) => {
  const [url, data, config = {}] = Array.isArray(args) ? args : [args];
  try {
    const response = await axiosService.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// endpoints
export const api = {
  auth: {
    login: (data) => axiosService.post("/api/auth/login/", data),
    logout: () => axiosService.post("/api/auth/logout/"),
    verifyOTP: (data) => axiosService.post("/api/auth/verify-otp/", data),
    refreshToken: () => axiosService.post("/api/auth/refresh/"),
    resetPassword: (data) =>
      axiosService.post("/api/auth/forgot-password/", data),
  },
  user: {
    getProfile: () => axiosService.get("/api/account/profile"),
    updateProfile: (data) => axiosService.put("/api/account/profile", data),
  },
  notifications: {
    getAll: () => axiosService.get("/api/notifications/"),
    getUnread: () => axiosService.get("/api/notifications/unread/"),
    getCount: () => axiosService.get("/api/notifications/count/"),
    markAsRead: (id) =>
      axiosService.post(`/api/notifications/${id}/mark_as_read/`),
    markAllAsRead: () =>
      axiosService.post("/api/notifications/mark_all_as_read/"),
    create: (data) => axiosService.post("/api/notifications/", data),
    delete: (id) => axiosService.delete(`/api/notifications/${id}/`),
  },
  images: {
    getProxiedImage: (imageUrl) => {
      // Use the correct content type for images
      return axiosService.get(
        `/api/proxy-image/?url=${encodeURIComponent(imageUrl)}`,
        {
          responseType: "blob",
        },
      );
    },
  },
  advertising: {
    getBanners: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return axiosService.get(
        `/api/advertising/banners/${queryParams ? `?${queryParams}` : ""}`,
      );
    },
    getBanner: (id) => axiosService.get(`/api/advertising/banners/${id}/`),
    createBanner: (data) =>
      axiosService.post("/api/advertising/banners/", data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    updateBanner: (id, data) =>
      axiosService.patch(`/api/advertising/banners/${id}/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    deleteBanner: (id) =>
      axiosService.delete(`/api/advertising/banners/${id}/`),
    getActiveBanners: (params = {}) => {
      const queryParams = new URLSearchParams(params).toString();
      return axiosService.get(
        `/api/advertising/banners/active_banners/${queryParams ? `?${queryParams}` : ""}`,
      );
    },
    getBannerTypes: () =>
      axiosService.get("/api/advertising/banners/banner_types/"),
  },
};
